import React, { useEffect, useMemo, useState } from "react";

import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  TextField,
  Typography,
  FilterOptionsState
} from "@mui/material";

import { useUsers } from "@/hooks";
import { Activation, Audience, User } from "@/types";

export interface ShareDialogProps {
  selectedEntity: Audience | Activation;
  entityType: "Audience" | "Activation";
  open: boolean;
  onClose: () => void;
  onShare: (ids: number[]) => void;
}

const ShareDialog: React.FC<ShareDialogProps> = ({
  selectedEntity,
  entityType,
  open,
  onClose,
  onShare,
}) => {
  const { users: allUsers, loading } = useUsers();

  // Users the entity is already shared with, until the selection is edited
  const [editedUsers, setEditedUsers] = useState<User[] | null>(null);
  const currentUsers = useMemo(() => {
    const shared = allUsers.filter((user) => selectedEntity.shared_with?.includes(user.id));
    return Array.from(new Map(shared.map((user) => [user.id, user])).values());
  }, [allUsers, selectedEntity]);
  const sharedUsers = editedUsers ?? currentUsers;

  useEffect(() => {
    setEditedUsers(null);
  }, [selectedEntity]);

  const filterUsers = (options: User[], state: FilterOptionsState<User>) => {
    // Get input value
    const search = state.inputValue.trim().toLowerCase();

    // Return everything if no input
    if (!search) return options;

    // Filter the options
    return options.filter((user) => {
      const nameMatch = user.name.toLowerCase().includes(search);
      const emailMatch = user.email.toLowerCase().includes(search);
      const groupMatch = user.group.toLowerCase().includes(search);

      return nameMatch || emailMatch || groupMatch;
    });
  };

  const handleShare = () => {
    const users = sharedUsers.map((user) => user.id);
    onShare(users);
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Share {entityType}</DialogTitle>
      <DialogContent sx={{ width: 400 }}>
        {loading ? (
          <Skeleton variant="rounded" height={56} />
        ) : (
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={allUsers}
            groupBy={(option) => option.group}
            getOptionLabel={(option) => option.name}
            value={sharedUsers}
            onChange={(event, newValue) => {
              // Dedupe by ID
              const deduped = Array.from(
                new Map(newValue.map((user) => [user.id, user])).values()
              );

              setEditedUsers(deduped);
            }}
            filterOptions={filterUsers}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.name}
                  label={option.name}
                  color="primary"
                  variant="outlined"
                  avatar={
                    <img
                      src={option.image}
                      alt={option.name}
                      style={{ width: 20, height: 20, borderRadius: "50%" }}
                    />
                  }
                />
              ))
            }
            renderOption={(props, option) => (
              <Box
                {...props}
                key={option.name}
                component="li"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <img
                  src={option.image}
                  alt={option.name}
                  style={{
                    width: 35,
                    height: 35,
                    borderRadius: "50%",
                    marginRight: 8,
                  }}
                />
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography variant={"body1"}>{option.name}</Typography>
                  <Typography variant={"body2"} color={"textSecondary"}>
                    {option.email}
                  </Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                fullWidth
                placeholder="Select Users"
              />
            )}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant={"contained"}
          size={"small"}
          disabled={loading}
          onClick={handleShare}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareDialog;
